import {
    GoogleAuthProvider,
    FacebookAuthProvider,
    signInWithPopup,
    signInWithRedirect,
    getRedirectResult,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    sendPasswordResetEmail,
    sendEmailVerification,
    fetchSignInMethodsForEmail,
    linkWithCredential,
    onAuthStateChanged,
    signOut,
} from "firebase/auth";
import {auth} from "../firebase";

export const LEGACY_JWT_KEY = "jwt";
const PENDING_CREDENTIAL_KEY = "pending_auth_credential";

export const PROVIDERS = {
    google: "google.com",
    facebook: "facebook.com",
    password: "password",
};

// В установленной PWA и на мобильных popup ненадёжен (iOS открывает его в Safari
// и не возвращает результат), поэтому там используется redirect на наш же домен.
export const isStandalone = () =>
    (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
    window.navigator.standalone === true ||
    document.referrer.includes('android-app://');

export const shouldUseRedirect = () => {
    const mobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ||
        (navigator.maxTouchPoints > 2 && /Mac|Linux/.test(navigator.userAgent));
    return isStandalone() || mobile;
};

const makeProvider = (providerId) => {
    if (providerId === PROVIDERS.google) {
        const p = new GoogleAuthProvider();
        p.setCustomParameters({prompt: 'select_account'});
        return p;
    }
    if (providerId === PROVIDERS.facebook) {
        const p = new FacebookAuthProvider();
        p.addScope('email');
        p.setCustomParameters({display: 'popup'});
        return p;
    }
    throw new Error("Unknown provider " + providerId);
};

const savePendingCredential = (credential) => {
    try {
        if (credential) sessionStorage.setItem(PENDING_CREDENTIAL_KEY, JSON.stringify(credential.toJSON()));
    } catch (e) { /* ignore */ }
};

const popPendingCredential = () => {
    try {
        const raw = sessionStorage.getItem(PENDING_CREDENTIAL_KEY);
        if (!raw) return null;
        sessionStorage.removeItem(PENDING_CREDENTIAL_KEY);
        const json = JSON.parse(raw);
        if (json.providerId === PROVIDERS.google) return GoogleAuthProvider.credentialFromJSON(json);
        if (json.providerId === PROVIDERS.facebook) return FacebookAuthProvider.credentialFromJSON(json);
    } catch (e) { /* ignore */ }
    return null;
};

// Если пользователь до этого пытался войти через провайдер, чей email уже занят другим
// способом входа, после успешного входа "старым" способом привязываем новый провайдер.
const linkPendingCredential = async (user) => {
    const pending = popPendingCredential();
    if (!pending || !user) return;
    try {
        await linkWithCredential(user, pending);
    } catch (e) {
        console.log('linkWithCredential failed: ' + (e && e.code));
    }
};

// Ошибка auth/account-exists-with-different-credential → говорим пользователю, каким способом
// входить, и запоминаем credential, чтобы привязать его после входа.
const describeAccountConflict = async (error) => {
    const email = error?.customData?.email;
    let credential = null;
    try {
        credential = FacebookAuthProvider.credentialFromError(error) || GoogleAuthProvider.credentialFromError(error);
    } catch (e) { /* ignore */ }
    savePendingCredential(credential);
    let methods = [];
    if (email) {
        try {
            methods = await fetchSignInMethodsForEmail(auth, email);
        } catch (e) { /* email enumeration protection may hide methods */ }
    }
    const err = new Error("account-exists-with-different-credential");
    err.code = "auth/account-exists-with-different-credential";
    err.email = email;
    err.methods = methods;
    return err;
};

export const signInWithProvider = async (providerId) => {
    const provider = makeProvider(providerId);
    if (window.saveDeeplinkParamsBeforeAuth) window.saveDeeplinkParamsBeforeAuth();

    if (shouldUseRedirect()) {
        await signInWithRedirect(auth, provider);
        return null; // страница уйдёт на redirect
    }

    try {
        const result = await signInWithPopup(auth, provider);
        await linkPendingCredential(result.user);
        return result.user;
    } catch (error) {
        if (error?.code === 'auth/popup-blocked' || error?.code === 'auth/operation-not-supported-in-this-environment') {
            await signInWithRedirect(auth, provider);
            return null;
        }
        if (error?.code === 'auth/account-exists-with-different-credential') {
            throw await describeAccountConflict(error);
        }
        if (error) error.viaProvider = providerId;
        throw error;
    }
};

// Firebase сразу логинит пользователя после createUserWithEmailAndPassword и пускает по паролю
// без подтверждения почты. Аккаунт только с паролем и неподтверждённым email считаем незалогиненным.
export const needsEmailVerification = (user) => {
    if (!user || user.emailVerified) return false;
    const providers = user.providerData || [];
    return providers.length > 0 && providers.every((p) => p.providerId === PROVIDERS.password);
};

const emailNotVerifiedError = (email) => {
    const err = new Error("email-not-verified");
    err.code = "auth/email-not-verified";
    err.email = email;
    return err;
};

export const signInWithEmail = async (email, password) => {
    const result = await signInWithEmailAndPassword(auth, email.trim(), password);
    if (needsEmailVerification(result.user)) {
        await signOut(auth);
        throw emailNotVerifiedError(result.user.email);
    }
    await linkPendingCredential(result.user);
    return result.user;
};

// Создаёт аккаунт, отправляет письмо и сразу разлогинивает: войти можно только после подтверждения.
// Отложенный credential соцсети не привязываем к неподтверждённому аккаунту — он привяжется при входе.
export const registerWithEmail = async (email, password) => {
    const result = await createUserWithEmailAndPassword(auth, email.trim(), password);
    try {
        await sendEmailVerification(result.user);
    } finally {
        await signOut(auth);
    }
    return result.user;
};

// Письмо можно отправить только от имени пользователя, поэтому на время отправки входим и выходим.
export const resendVerificationEmail = async (email, password) => {
    const result = await signInWithEmailAndPassword(auth, email.trim(), password);
    try {
        if (needsEmailVerification(result.user)) await sendEmailVerification(result.user);
    } finally {
        await signOut(auth);
    }
};

export const resetPassword = (email) => sendPasswordResetEmail(auth, email.trim());

export const signOutUser = () => signOut(auth);

// Завершение redirect-входа после возврата на страницу. Возвращает user или null.
export const completeRedirectSignIn = async () => {
    try {
        const result = await getRedirectResult(auth);
        if (result && result.user) {
            await linkPendingCredential(result.user);
            return result.user;
        }
        return null;
    } catch (error) {
        if (error?.code === 'auth/account-exists-with-different-credential') {
            throw await describeAccountConflict(error);
        }
        if (error) error.viaProvider = 'redirect';
        throw error;
    }
};

// Неподтверждённый email-пользователь для приложения — гость (в т.ч. сессия, сохранённая до этой проверки).
export const subscribeAuthState = (callback) => onAuthStateChanged(auth, (user) => {
    if (needsEmailVerification(user)) {
        callback(null);
        return;
    }
    callback(user);
});

export const getIdToken = async (forceRefresh = false) => {
    const user = auth.currentUser;
    if (!user || needsEmailVerification(user)) return null;
    try {
        return await user.getIdToken(forceRefresh);
    } catch (e) {
        console.log('getIdToken failed: ' + (e && e.code));
        return null;
    }
};

export const getProviderId = (user) => {
    if (!user) return null;
    const info = (user.providerData || [])[0];
    return info ? info.providerId : null;
};

export const getDisplayLabel = (user) => {
    if (!user) return '';
    return user.displayName || user.email || '';
};

// Старый Xsolla JWT в localStorage: нужен только чтобы один раз показать
// сообщение «войдите заново» и подставить email в форму.
const parseJwtPayload = (token) => {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(window.atob(base64).split('').map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(json);
};

export const readLegacyXsollaJwt = () => {
    try {
        const raw = localStorage.getItem(LEGACY_JWT_KEY);
        if (!raw || raw === 'null' || raw.length < 10) return null;
        return parseJwtPayload(raw);
    } catch (e) {
        return null;
    }
};

export const clearLegacyXsollaJwt = () => {
    try {
        localStorage.removeItem(LEGACY_JWT_KEY);
    } catch (e) { /* ignore */ }
};

// Unity WebGL регистрирует обработчики клавиатуры на window в capture-фазе и отменяет ввод
// (captureAllKeyboardInput), поэтому в HTML-поля рядом с канвасом нельзя печатать.
// Наш listener регистрируется раньше Unity (модуль грузится при старте React) и для событий из
// диалогов входа останавливает дальнейшую обработку. Действие по умолчанию (ввод символа,
// submit по Enter) при этом сохраняется. Escape пропускаем, чтобы MUI Dialog мог закрыться.
const KEYBOARD_GUARD_SELECTOR = '.login-dialog, .legacy-login-dialog';
const guardKeyboardFromUnity = (event) => {
    if (event.key === 'Escape') return;
    const target = event.target;
    if (target && typeof target.closest === 'function' && target.closest(KEYBOARD_GUARD_SELECTOR)) {
        event.stopImmediatePropagation();
    }
};
['keydown', 'keyup', 'keypress'].forEach((type) => {
    window.addEventListener(type, guardKeyboardFromUnity, true);
});

// Мост для legacy-кода в public/js/main.js (isAuthenticated для Unity, Zendesk-форма).
window.pwaAuth = {
    isAuthenticated: () => !!auth.currentUser && !needsEmailVerification(auth.currentUser),
    getUser: () => (needsEmailVerification(auth.currentUser) ? null : auth.currentUser),
    getIdToken,
    openLoginDialog: () => window.dispatchEvent(new CustomEvent('onShowLoginEvent')),
};
